import { api, dismiss, getQueue, mutate, mutateUpload } from './api'
import { getMe, pricesHidden } from './me'
import { blockedSaveOf, clear, dayLabel, el, isOutOfTime, money, OUT_OF_TIME, shortDate, toast, todayISO } from './ui'
import './day-close.css'
import { sendAllOn } from './send-all-on'
import { roundConfig } from './round-config'
import { slideToConfirm } from './slide-confirm'
import { buzz } from './haptic'
import { reducedMotion } from './motion'
import { workTitle } from './trade'
import { stepBar, type Step } from './enquiry-steps'

// Finish my day (0064; Liam, 12 Oct 2026, the design he signed off: docs/WORKER-PLAN.md "Finish my day"). The person who
// does the jobs finishes their day from Today once every job is dealt with: every job, or why not; a note, photos and a
// voice note; the cash they hand over; and, when the owner has set their pay, their invoice to the owner. The owner
// sees each finished day (Today and the Diary) and answers it: seen, cash received, invoice paid. An owner who does
// the jobs finishes their own day the same way, worded for themselves, with no invoice and no one to tell.

export interface DayJob { id: number; name: string; street: string; done: boolean; why: string | null }
export interface DayInvoice {
  no: string; date: string
  from: { name: string; address: string | null; bank: string | null; utr: string | null }
  to: { name: string }
  kind: 'day' | 'commission'; rate: number; min_pence?: number | null; work_pence: number | null; pay_pence: number; done_count: number
  jobs: { name: string; street: string }[]
}
export interface DayClose {
  id: number; version: number; date: string; worker: string; closed_by: string; all_done: 0 | 1; note: string | null
  job_count: number; done_count: number; not_done_count: number; work_pence: number | null; jobs: DayJob[]
  cash_expected_pence: number | null; cash_pence: number | null
  invoice_no: string | null; invoice_pence: number | null; invoice: DayInvoice | null; has_logo: boolean
  seen_at: string | null; cash_received_at: string | null; invoice_paid_at: string | null; reopened_at: string | null
  media: { id: number; kind: 'photo' | 'audio' }[]
}
// The visits of the day as Today holds them (screens/today.ts TodayClean): what finishing needs of them.
export interface FinishVisit {
  id: number; version: number; state: string; customer_name?: string | null; customer_address?: string | null
  amount_pence?: number | null; payment_status?: string | null; payment_method?: string | null
  cash_collected_pence?: number | null; blocked_reason?: string | null; blocked_note?: string | null
  // Money brought from Squeegee (0043), on a round that has it: what Today reads for cash taken.
  carryover?: number | null; cash_taken_pence?: number | null; cash_unconfirmed?: number | null; from_credit_pence?: number | null
  confirmed?: number | null; time_slot?: string | null; worker?: string | null; started_at?: string | null
}

// Close-out's unassigned-plan exception: no agreement, time, worker, start or Couldn't do it.
// This narrow close-out exception does not define the Diary's day-only Pencilled count. Finishing the day never
// asks about it (Liam, 13 Oct 2026: "I cant close out my day because of these pencilled or unconfirmed jobs"); the
// server's own rule is functions/lib/day-close.ts PENCILLED_SQL.
export function pencilledIn(v: FinishVisit): boolean {
  return v.state === 'booked' && !v.confirmed && !(v.time_slot ?? '').trim() && !(v.worker ?? '').trim() && !v.started_at && !v.blocked_reason
}
interface MyInvoice {
  worker_name: string | null; pay_kind: 'day' | 'commission' | null; pay_amount: number | null
  invoice_name: string | null; invoice_address: string | null; invoice_bank: string | null; invoice_utr: string | null
  invoice_auto: 0 | 1; has_logo: boolean
}

// Today's own reasons, in its order (Liam, 24 Oct 2026, finishing early: "just list weather and other" for rained off or
// anything else; Customer cancelled joins them, as on a stop's Couldn't do it).
const REASONS: [string, string][] = [['time', 'Ran out of time'], ['not_in', 'Not in'], ['no_access', 'No access'], ['weather', 'Weather'], ['customer_cancelled', 'Customer cancelled'], ['other', 'Other']]
const REASON_WORDS: Record<string, string> = { not_in: 'not in', no_access: 'no access', weather: 'weather', customer_cancelled: 'customer cancelled', time: 'ran out of time', other: 'other' }

const isOwner = (): boolean => getMe()?.role === 'owner'
const first = (name: string): string => name.trim().split(/\s+/)[0] || name
const streetOf = (address: string | null | undefined): string => (address ?? '').split(',')[0]!.trim()

// The finished day of a date for the person signed in, as last read (null: not finished; undefined: not read yet).
const mine = new Map<string, DayClose | null>()
async function readMine(date: string): Promise<DayClose | null> {
  const r = await api<{ closes: DayClose[] }>(`/api/day-closes?date=${date}`)
  const name = getMe()?.workerName?.trim().toLowerCase()
  const found = r.closes.find((c) => c.worker.trim().toLowerCase() === name) ?? null
  mine.set(date, found)
  return found
}

// The cash Roundly has them taking (the server's own rule, functions/lib/day-close.ts).
function cashTaken(visits: FinishVisit[]): number {
  return visits.filter((v) => v.state === 'done').reduce((sum, v) => {
    if (v.carryover === 1) return sum
    if ((v.cash_taken_pence ?? 0) > 0) return sum + v.cash_taken_pence!
    if ((v.cash_unconfirmed ?? 0) > 0) return sum
    if (v.cash_collected_pence !== null && v.cash_collected_pence !== undefined) return sum + v.cash_collected_pence
    return sum + (v.payment_status === 'paid' && v.payment_method === 'cash' ? Math.max(0, (v.amount_pence ?? 0) - (v.from_credit_pence ?? 0)) : 0)
  }, 0)
}
const notDealtWith = (visits: FinishVisit[]): FinishVisit[] => visits.filter((v) => v.state === 'booked' && !v.blocked_reason)

// ---- Today: the card at the foot of their day ------------------------------------------------------------------
// Shown on today once every job is dealt with (done, or "couldn't do it"), and on a day of the last week left
// unfinished. Once finished: "Day finished", with their invoice.
export function finishDayCard(container: HTMLElement, date: string, allVisits: FinishVisit[], rerender: () => void): void {
  const visits = allVisits.filter((v) => !pencilledIn(v))
  const today = todayISO()
  // Only someone whose name is on visits has a day to finish.
  if (!getMe()?.workerName?.trim() || !visits.length || date > today || date < weekBefore(today)) return
  const slot = el('div', { class: 'day-close-slot' })
  container.append(slot)
  const draw = (close: DayClose | null): void => {
    clear(slot)
    if (close && !close.reopened_at) { slot.append(finishedCard(close, () => { mine.delete(date); rerender() })); return }
    const left = notDealtWith(visits)
    if (date === today && left.length) return
    const card = el('section', { class: 'day-close-card', 'aria-label': 'Finish my day' },
      el('h3', {}, close?.reopened_at ? 'Reopened for you to finish again' : date === today ? 'Every job dealt with' : `${dayLabel(date)} is not finished`),
      el('p', { class: 'muted' }, isOwner() ? 'Finish your day: anything to note, and the cash you took.' : 'Finish your day: say how it went, hand over the cash, and send your invoice.'))
    const go = el('button', { type: 'button', class: 'btn-primary day-close-go' }, 'Finish my day')
    go.addEventListener('click', () => openFinishDay(date, visits, () => { mine.delete(date); rerender() }))
    card.append(go)
    slot.append(card)
  }
  const known = mine.get(date)
  if (known !== undefined) draw(known)
  void readMine(date).then((c) => { if (slot.isConnected && c !== known) draw(c) }).catch(() => { /* no signal: the card waits */ })
}
function weekBefore(iso: string): string {
  const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10)
}

function finishedCard(close: DayClose, changed: () => void): HTMLElement {
  const card = el('section', { class: 'day-close-card is-finished', 'aria-label': 'Day finished' },
    el('h3', {}, 'Day finished'),
    el('p', { class: 'muted' }, [`${close.done_count} of ${close.job_count} done`, close.cash_pence !== null && !pricesHidden() ? `${money(close.cash_pence)} cash` : null, close.invoice_no ? `invoice ${close.invoice_no} sent` : null].filter(Boolean).join(' · ')))
  const row = el('div', { class: 'day-close-grid' })
  if (close.invoice) {
    const see = el('button', { type: 'button', class: 'btn-secondary' }, 'See my invoice')
    see.addEventListener('click', () => openInvoice(close))
    row.append(see)
  }
  // Photos left behind for want of signal, or one more: added to the finished day.
  const pick = el('input', { type: 'file', accept: 'image/*', multiple: '', class: 'day-close-file', 'aria-label': 'Add photos to the day' }) as HTMLInputElement
  const add = el('button', { type: 'button', class: 'btn-secondary' }, 'Add photos')
  add.addEventListener('click', () => pick.click())
  pick.addEventListener('change', () => { void uploadAll(close.id, [...(pick.files ?? [])], null).then((failed) => { toast(failed ? `${failed} did not send. Try again with signal.` : 'Added to your day'); changed() }) })
  row.append(add, pick)
  card.append(row)
  return card
}

async function uploadAll(closeId: number, photos: File[], voice: Blob | null): Promise<number> {
  const r = await uploadEach(closeId, photos, voice)
  return r.photos + (r.voice ? 1 : 0)
}
// What did not send: how many photos, and whether the voice note (the card can take photos again, not a voice note).
async function uploadEach(closeId: number, photos: File[], voice: Blob | null): Promise<{ photos: number; voice: boolean }> {
  let lost = 0
  for (const f of photos) {
    const up = await mutateUpload({ path: `/api/day-closes/${closeId}/media`, key: crypto.randomUUID(), fields: {}, file: f, filename: f.name || 'photo.jpg', fileField: 'file', ackField: 'row' })
    if (!up.ok) lost++
  }
  let voiceLost = false
  if (voice) {
    const up = await mutateUpload({ path: `/api/day-closes/${closeId}/media`, key: crypto.randomUUID(), fields: {}, file: voice, filename: 'voice.m4a', fileField: 'file', ackField: 'row' })
    voiceLost = !up.ok
  }
  return { photos: lost, voice: voiceLost }
}

// ---- The three steps ----------------------------------------------------------------------------------------------
export function sheet(title: string): { dialog: HTMLDialogElement; head: HTMLElement; body: HTMLElement; close: () => void } {
  const dialog = el('dialog', { class: 'booking-dialog day-close-dialog', 'aria-label': title }) as HTMLDialogElement
  const head = el('div', { class: 'day-close-head' })
  const shut = el('button', { type: 'button', class: 'btn-secondary day-close-shut' }, 'Close')
  const body = el('div', { class: 'day-close-body' })
  dialog.append(el('header', { class: 'booking-head' }, head, shut), body)
  const close = (): void => { if (dialog.open) dialog.close() }
  shut.addEventListener('click', close)
  dialog.addEventListener('close', () => dialog.remove())
  document.body.append(dialog)
  dialog.showModal()
  dialog.setAttribute('tabindex', '-1'); dialog.focus({ preventScroll: true })
  return { dialog, head, body, close }
}
const heading = (step: string, title: string): HTMLElement[] => [el('div', { class: 'day-close-step' }, step), el('h2', {}, title)]

export function openFinishDay(date: string, visits: FinishVisit[], done: () => void): void {
  const owner = isOwner()
  const s = sheet('Finish my day')
  const why = new Map<number, string>()
  // Queued reasons keep the existing local flow, but are not saved evidence for green.
  const queuedReasons = new Set(visits.filter(v => getQueue().some(q => q.path === `/api/cleans/${v.id}` && q.method === 'PATCH' && !!(q.body as Partial<FinishVisit> | null)?.blocked_reason)).map(v => v.id))
  const barSlot = el('div', { class: 'day-close-progress' })
  let note = ''
  const photos: File[] = []
  let voice: Blob | null = null
  let cash = cashTaken(visits)
  let cashSaid: number | null = pricesHidden() ? null : cash
  let invoice: MyInvoice | null = null
  // What the server would put on their invoice now (its own count, so a worker who cannot see prices is never shown the
  // day's total): read before step 3.
  type Preview = { pay_pence: number | null; kind: string | null; rate: number | null; done_count: number }
  let preview = null as Preview | null
  let send = true
  // Where the sheet is: a voice note that ends on another step keeps its recording without jumping back to step 2, and
  // while the day sends nothing can send it twice (review, 12 Oct 2026).
  let at: 1 | 2 | 3 | 'finishing' | 'done' = 1
  let stopVoice: (() => Promise<void>) | null = null
  let voiceStarting: Promise<unknown> | null = null
  let failWords = ''
  s.dialog.addEventListener('cancel', (e) => { if (at === 'finishing') e.preventDefault() })
  // Closed with the microphone on (or while the phone asks for it): it stops (second review, 12 Oct 2026).
  s.dialog.addEventListener('close', () => { void stopVoice?.(); void voiceStarting?.then(() => stopVoice?.()) })
  const shutButton = (): HTMLButtonElement | null => s.dialog.querySelector<HTMLButtonElement>('.day-close-shut')
  // A finish that did not go says why on the sheet itself, above the slide (a toast sits behind an open sheet).
  const failLine = (into: HTMLElement = s.body): void => { if (failWords) { into.append(el('p', { class: 'day-close-warn', role: 'alert' }, failWords)); failWords = '' } }
  const steps = (): number => (invoice?.pay_kind && invoice.pay_amount !== null && !owner && visits.some((v) => v.state === 'done') ? 3 : 2)
  // Whether there is a third step (their invoice) is known only once this read has answered, or failed (no signal). Until
  // then nothing is decided from it: step 1 says "step 1", not "1 of 2", and step 2 waits for the answer before it offers
  // the slide or Next (Liam's notes, 15 Oct 2026: on a slow signal a worker who tapped through quickly was handed the finish
  // slide and never saw their invoice). Someone with no invoice step in any case (an owner, or no job done) never waits.
  let invoiceBack = false
  let whenInvoiceBack: (() => void) | null = null
  const waitingForInvoice = (): boolean => !invoiceBack && !owner && visits.some((v) => v.state === 'done')
  const stepWords = (n: 1 | 2): string => (waitingForInvoice() ? `Finish my day · step ${n}` : `Finish my day · ${n} of ${steps()}`)
  const relabel = (n: 1 | 2): void => { const label = s.head.querySelector('.day-close-step'); if (label) label.textContent = stepWords(n); drawBar() }
  void api<{ row: MyInvoice }>('/api/me/invoice')
    .then((r) => { invoice = r.row; send = r.row.invoice_auto === 1 })
    .catch(() => { invoice = null })
    .then(() => { invoiceBack = true; whenInvoiceBack?.() })

  // The invoice is marked skipped only once the worker has seen its step or finished (review, 2 Nov 2026).
  let sawInvoiceStep = false
  const drawBar = (saved: DayClose | null = null, noteSaved = true): void => {
    clear(barSlot)
    s.body.prepend(barSlot)
    // The number of tracks is unknown until the invoice read returns. A neutral loading line makes no choice.
    if (waitingForInvoice() && at !== 'done') {
      barSlot.append(el('div', { class: 'day-close-progress-loading', role: 'status', 'aria-busy': 'true' }, 'Getting your invoice…'))
      return
    }
    const ended = at === 'done'
    if (at === 3) sawInvoiceStep = true
    const accounted = saved ? saved.jobs.every(j => j.done || !!j.why) : visits.every(v => v.state === 'done' || v.state === 'booked' && !!v.blocked_reason && !queuedReasons.has(v.id))
    const hasInvoiceStep = !owner && (!!saved?.invoice || steps() === 3 || !invoice && visits.some(v => v.state === 'done'))
    const words = owner ? ['Every job done', 'Anything to note'] : ['Jobs done', 'Anything to pass on']
    const states: Step[] = [
      { key: 'jobs', word: words[0]!, state: accounted ? 'done' : !ended && at === 1 ? 'next' : 'unknown', detail: 'Every job is done or has a saved reason it could not be done.', waitingFor: 'Waiting for every job to be done or a saved reason it could not be done.' },
      { key: 'note', word: words[1]!, state: saved && noteSaved ? 'done' : !ended && at !== 1 && at !== 3 ? 'next' : 'unknown', detail: 'Saved with your finished day. Photos and voice notes send separately.', waitingFor: ended ? 'Your day is waiting to send. Anything typed here has not been saved yet.' : 'Saved when you finish your day.' },
    ]
    if (hasInvoiceStep) states.push({ key: 'invoice', word: 'Your invoice', state: saved?.invoice ? 'done' : invoice && !send && (ended || sawInvoiceStep) ? 'skipped' : !ended && at === 3 ? 'next' : 'unknown', detail: 'Your invoice is saved with your finished day.', waitingFor: ended ? 'No saved invoice was returned.' : 'Waiting for your invoice to be saved when you finish your day.' })
    // Before choosing to send, the invoice is still the next action even when the note has not yet been saved.
    if (!ended && !states.some(step => step.state === 'next')) {
      const pending = states.find(step => step.state === 'unknown')
      if (pending && !(pending.key === 'invoice' && !invoice)) pending.state = 'next'
    }
    barSlot.append(stepBar(states, { label: 'Finish my day steps', ended: ended || !states.some(step => step.state === 'next') }))
    const reasonWord = (reason: string, visit?: FinishVisit): string => visit && isOutOfTime(visit.blocked_reason, visit.blocked_note) ? 'Ran out of time' : REASONS.find(([key]) => key === reason)?.[1] ?? 'Other'
    // The saved snapshot already names Ran out of time as 'time'; never borrow a new local reason for a 409.
    const blocked = saved ? saved.jobs.filter(j => !j.done && j.why).map(j => reasonWord(j.why!))
      : visits.filter(v => v.state === 'booked' && v.blocked_reason && !queuedReasons.has(v.id)).map(v => reasonWord(v.blocked_reason!, v))
    if (blocked.length) barSlot.append(el('p', { class: 'day-close-warn day-close-progress-reasons' }, `${blocked.length} couldn't do · ${[...new Set(blocked)].join(' · ')}`))
  }

  const step1 = (): void => {
    at = 1; void stopVoice?.()
    clear(s.head); clear(s.body)
    whenInvoiceBack = () => { if (at === 1) relabel(1) }
    const left = notDealtWith(visits)
    // A question only when there is something to answer (Liam, 14 Oct 2026: "it asks a question with no way to reply").
    s.head.append(...heading(stepWords(1), left.length ? 'Did you finish every job?' : 'Every job done'))
    const blocked = visits.filter((v) => v.state === 'booked' && v.blocked_reason)
    const doneN = visits.filter((v) => v.state === 'done').length
    if (!left.length) {
      s.body.append(el('p', { class: 'day-close-lead' }, `${doneN} done${blocked.length ? `, ${blocked.length} you couldn't do (${blocked.map((v) => `${first(v.customer_name ?? 'one')}, ${isOutOfTime(v.blocked_reason, v.blocked_note) ? 'ran out of time' : REASON_WORDS[v.blocked_reason!] ?? 'other'}`).join('; ')})` : ''}.`))
    } else {
      s.body.append(el('p', { class: 'muted' }, owner ? 'These weren\'t done. Say why for each; then Move to another day on their card.' : 'These weren\'t done. Say why for each, so the owner can put them on another day.'))
      for (const v of left) {
        const row = el('div', { class: 'day-close-job' }, el('div', {}, el('strong', {}, v.customer_name ?? 'Visit'), el('span', { class: 'muted' }, streetOf(v.customer_address))))
        const chips = el('div', { class: 'day-close-chips', role: 'group', 'aria-label': `Why ${v.customer_name ?? 'this one'} wasn't done` })
        for (const [key, label] of REASONS) {
          const chip = el('button', { type: 'button', class: `day-close-chip${why.get(v.id) === key ? ' is-on' : ''}`, 'aria-pressed': String(why.get(v.id) === key) }, label)
          chip.addEventListener('click', () => { why.set(v.id, key); step1() })
          chips.append(chip)
        }
        row.append(chips)
        s.body.append(row)
      }
    }
    const next = el('button', { type: 'button', class: 'btn-primary day-close-next' }, 'Next')
    next.addEventListener('click', () => { void saveReasons().then(step2) })
    s.body.append(next)
    drawBar()
  }
  // Each job not done gets its "couldn't do it" as Today records one (worker plan step 4); "Ran out of time" is Other
  // with those words. One left without an answer stays as it is.
  const saveReasons = async (): Promise<void> => {
    for (const v of notDealtWith(visits)) {
      const key = why.get(v.id)
      if (!key) continue
      const body = key === 'time' ? { ...blockedSaveOf('time', ''), version: v.version } : { blocked_reason: key, version: v.version }
      const ack = await mutate<FinishVisit>({ label: `Couldn't do it: ${v.customer_name ?? ''}`, method: 'PATCH', path: `/api/cleans/${v.id}`, body })
      if (!ack.ok && ack.status === 0) queuedReasons.add(v.id)
      if (ack.ok) queuedReasons.delete(v.id)
      if (ack.ok || ack.status === 0) { v.blocked_reason = key === 'time' ? 'other' : key; v.blocked_note = key === 'time' ? OUT_OF_TIME : v.blocked_note; if (ack.row) v.version = ack.row.version }
    }
  }

  const step2 = (): void => {
    at = 2
    clear(s.head); clear(s.body)
    s.head.append(...heading(stepWords(2), owner ? 'Anything to note?' : 'Anything to pass on?'))
    const id = `day-note-${date}`
    const box = el('textarea', { id, rows: '4', class: 'day-close-note' }) as HTMLTextAreaElement
    box.value = note
    box.addEventListener('input', () => { note = box.value })
    // "A note for the business" (Liam, 12 Oct 2026: "more universal makes workers feel more involved").
    s.body.append(el('label', { for: id, class: 'day-close-label' }, owner ? 'A note for this day ' : 'A note for the business ', el('span', { class: 'muted' }, '(optional)')), box)
    // The started card's round buttons, each with its word under it (Liam: "should we use icons for voice notes and
    // photos tho it may get missed").
    const tools = el('div', { class: 'live-tools dc-tools' })
    const recording = !!stopVoice || !!voiceStarting
    const rec = el('button', { type: 'button', class: recording ? 'ic ic-stop is-on voice-recording' : `ic ic-mic${voice ? ' live-on' : ''}` }, recording ? 'Recording… tap to stop' : voice ? 'Voice note kept' : 'Voice note')
    // One tap starts it, the next stops it (review: the second tap used to start another recording as well). A
    // recording that comes to life after its step has gone (Next, Back, Close while the phone asked) stops at once.
    rec.addEventListener('click', () => {
      if (stopVoice) { void stopVoice(); return }
      if (voiceStarting) return
      const starting = recordVoice(rec, (b) => { voice = b; stopVoice = null; if (at === 2) step2() })
      voiceStarting = starting
      void starting.then((stop) => {
        voiceStarting = null
        stopVoice = stop
        if (stop && (at !== 2 || !s.dialog.open)) void stop()
      })
    })
    const pick = el('input', { type: 'file', accept: 'image/*', multiple: '', class: 'day-close-file', 'aria-label': 'Add photos' }) as HTMLInputElement
    const addPhotos = el('button', { type: 'button', class: `ic ic-camera${photos.length ? ' live-on' : ''}` }, photos.length ? `${photos.length} photo${photos.length === 1 ? '' : 's'} added` : 'Photos')
    addPhotos.addEventListener('click', () => pick.click())
    pick.addEventListener('change', () => { for (const f of pick.files ?? []) if (photos.length < 10) photos.push(f); step2() })
    tools.append(rec, addPhotos, pick)
    s.body.append(tools)
    const cashBox = el('div', { class: 'day-close-cash' })
    if (pricesHidden()) {
      const input = el('input', { type: 'number', inputmode: 'decimal', min: '0', step: '0.01', id: `day-cash-${date}`, class: 'day-close-amount' }) as HTMLInputElement
      if (cashSaid !== null) input.value = (cashSaid / 100).toFixed(2)
      input.addEventListener('input', () => { const n = Math.round(Number(input.value) * 100); cashSaid = Number.isFinite(n) && n >= 0 ? n : null })
      cashBox.append(el('label', { for: input.id, class: 'day-close-label' }, owner ? 'Cash you took today' : 'Cash you\'re handing over'), input)
    } else {
      cashBox.append(el('div', { class: 'day-close-label' }, owner ? 'Cash taken today' : 'Cash to hand over'), el('div', { class: 'day-close-sum' }, money(cash)))
      const choice = el('div', { class: 'day-close-grid' })
      const right = el('button', { type: 'button', class: `btn-secondary${cashSaid === cash ? ' is-on' : ''}`, 'aria-pressed': String(cashSaid === cash) }, 'That\'s right')
      right.addEventListener('click', () => { cashSaid = cash; step2() })
      const other = el('button', { type: 'button', class: `btn-secondary${cashSaid !== cash ? ' is-on' : ''}`, 'aria-pressed': String(cashSaid !== cash) }, 'It\'s different')
      other.addEventListener('click', () => { cashSaid = cashSaid === cash ? null : cashSaid; step2(); s.body.querySelector<HTMLInputElement>('.day-close-amount')?.focus() })
      choice.append(right, other)
      cashBox.append(choice)
      if (cashSaid !== cash) {
        const input = el('input', { type: 'number', inputmode: 'decimal', min: '0', step: '0.01', 'aria-label': 'Cash you have', class: 'day-close-amount' }) as HTMLInputElement
        if (cashSaid !== null) input.value = (cashSaid / 100).toFixed(2)
        input.addEventListener('input', () => { const n = Math.round(Number(input.value) * 100); cashSaid = Number.isFinite(n) && n >= 0 ? n : null })
        cashBox.append(input)
      }
    }
    s.body.append(cashBox)
    // The foot of the step is drawn from what is known now, and again when the answer about their invoice lands (only
    // this part, so a note being typed keeps its place).
    const foot = el('div', { class: 'day-close-foot' })
    const drawFoot = (): void => {
      clear(foot)
      if (waitingForInvoice()) { foot.append(el('p', { class: 'day-close-sending', role: 'status' }, 'Getting your invoice…'), backLink(step1)); return }
      failLine(foot)
      // No answer (no signal): the finish carries no choice, so their own setting decides whether an invoice goes, as it
      // always did; they are told, so it is never a surprise.
      if (!owner && !invoice && visits.some((v) => v.state === 'done')) foot.append(el('p', { class: 'day-close-warn' }, 'No signal to show your invoice. It goes with your day if you send one when you finish.'))
      if (steps() === 2) {
        foot.append(finishSlider(step2, 2), backLink(step1))
      } else {
        const back = el('button', { type: 'button', class: 'btn-secondary' }, 'Back')
        back.addEventListener('click', step1)
        const next = el('button', { type: 'button', class: 'btn-primary' }, 'Next')
        next.addEventListener('click', step3)
        foot.append(el('div', { class: 'day-close-grid' }, back, next))
      }
    }
    whenInvoiceBack = () => { if (at === 2) { relabel(2); drawFoot() } }
    drawFoot()
    s.body.append(foot)
    drawBar()
  }
  // The end of the day is a slide, like finishing a job (Liam, 12 Oct 2026: "Finish my day should be more satisfying
  // maybe slide, its the end of the day its exciting!"). It cannot happen by a brush of the screen.
  // `from`: the step the slide was on. Back tapped in the moment between the slide and the send means it was not sent.
  const finishSlider = (again: () => void, from: 2 | 3): HTMLElement => slideToConfirm({ label: 'Slide to finish my day', doneLabel: 'Day finished', tone: 'lemon', onConfirm: () => { if (at === from && s.dialog.open) void finish(again) } })
  const backLink = (to: () => void): HTMLElement => {
    const b = el('button', { type: 'button', class: 'link-button day-close-back' }, 'Back')
    b.addEventListener('click', to)
    return b
  }

  const step3 = (): void => {
    at = 3; void stopVoice?.(); whenInvoiceBack = null
    clear(s.head); clear(s.body)
    s.head.append(...heading('Finish my day · 3 of 3', 'Your invoice for today'))
    const inv = invoice!
    const doneN = visits.filter((v) => v.state === 'done').length
    const detailsMissing = !inv.invoice_bank || !(inv.invoice_name || inv.worker_name)
    const total = el('span', {}, preview ? money(preview.pay_pence ?? 0) : '…')
    s.body.append(el('div', { class: 'day-close-invoice' },
      el('div', { class: 'day-close-row muted' }, el('span', {}, `To ${getMe()?.businessName ?? 'the owner'}`), el('span', {}, shortDate(date))),
      el('div', { class: 'day-close-row' }, el('span', {}, `${doneN} job${doneN === 1 ? '' : 's'} done`), el('strong', {}, inv.pay_kind === 'day' ? 'Day rate' : `${inv.pay_amount}% of the day’s work`)),
      el('div', { class: 'day-close-row day-close-total' }, el('span', {}, 'To pay you'), total)))
    if (!preview) void api<{ preview: Preview | null }>(`/api/day-closes?date=${date}&preview=1`).then((r) => { preview = r.preview; total.textContent = money(r.preview?.pay_pence ?? 0) }).catch(() => { total.textContent = 'worked out when it sends' })
    if (detailsMissing) s.body.append(el('p', { class: 'day-close-warn' }, 'Add your name and bank details so the owner can pay you.'))
    const auto = el('input', { type: 'checkbox', id: `day-send-${date}` }) as HTMLInputElement
    auto.checked = send
    auto.addEventListener('change', () => { send = auto.checked; drawBar() })
    s.body.append(el('label', { for: auto.id, class: 'day-close-toggle' }, 'Send my invoice when I finish', auto))
    const details = el('button', { type: 'button', class: 'link-button day-close-link' }, detailsMissing ? 'Add my invoice details' : 'Change my invoice details')
    details.addEventListener('click', () => openInvoiceDetails(() => { void api<{ row: MyInvoice }>('/api/me/invoice').then((r) => { invoice = r.row; step3() }) }))
    s.body.append(details)
    failLine()
    s.body.append(finishSlider(step3, 3), backLink(step2))
    drawBar()
  }

  const finish = async (again: () => void): Promise<void> => {
    if (at === 'finishing' || at === 'done') return
    at = 'finishing'
    // Held while the day itself sends: Back, Close and Escape wait, so it cannot be sent twice or closed half way.
    const shut = shutButton(); if (shut) shut.disabled = true
    const hold = (on: boolean): void => { s.body.querySelectorAll<HTMLButtonElement>('button').forEach((b) => { b.disabled = on }) }
    hold(true)
    const sending = el('p', { class: 'day-close-sending', role: 'status' }, 'Sending your day…')
    s.body.append(sending)
    const letGo = (why: string): void => { failWords = why; if (shut) shut.disabled = false; again() }
    try {
      // A voice note still recording (or still asking for the microphone) is stopped and goes with the day.
      if (voiceStarting) await voiceStarting
      if (stopVoice) await stopVoice()
      // Kept to send later (no signal, the server busy, an answer it could not read, signed out): the app's own
      // waiting changes send it, so it is said as kept, never as "try again" (a second slide would send it twice).
      let kept = false
      const ack = await mutate<DayClose>({
        label: `Finish my day: ${dayLabel(date)}`,
        method: 'POST',
        path: '/api/day-closes',
        // Their invoice choice when step 3 was shown; with no signal for their details, their own setting decides
        // (review: a failed read used to send "no invoice").
        body: { date, all_done: notDealtWith(visits).length === 0, note: note.trim() || null, cash_pence: cashSaid, ...(invoice && !owner ? { send_invoice: steps() === 3 && send } : {}) },
        onQueued: () => { kept = true },
      })
      // Finished already (another phone): nothing to save, so it is let go from the changes waiting to send.
      if (ack.status === 409) { const held = getQueue().find((q) => q.path === '/api/day-closes' && q.method === 'POST' && q.status === 'conflict' && (q.body as { date?: string } | undefined)?.date === date); if (held) dismiss(held.key) }
      if (!ack.ok && !kept && ack.status !== 409) {
        letGo(ack.status >= 400 && ack.status < 500 && ack.status !== 401 && ack.status !== 403 && ack.error ? ack.error : 'Your day did not send. Try the slide again.')
        return
      }
      // The day has gone (or is kept to go): Close works again while photos and a voice note follow.
      if (shut) shut.disabled = false
      const close = (ack.row ?? (ack.status === 409 ? (ack.current as unknown as DayClose | undefined) : undefined)) ?? null
      const media = photos.length + (voice ? 1 : 0)
      if (close?.id && media) sending.textContent = 'Sending your photos…'
      const lost = close?.id && media ? await uploadEach(close.id, photos, voice) : { photos: 0, voice: false }
      done()
      // The day, done (Liam, 12 Oct 2026: "when day is finished and invoice sent its should also be more
      // satisfying!"): a big green tick that pops with a burst of lemon, green and slate, and what went, line by line.
      const taken = ack.status === 409
      const doneN = visits.filter((v) => v.state === 'done').length
      const notDoneN = visits.filter((v) => v.state !== 'done' && v.state !== 'cancelled').length
      const and = (parts: string[]): string => parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0] ?? ''
      const photosSent = photos.length - lost.photos
      // What went: the note rides with the day; photos and a voice note as each one sent.
      const sent = and([...(note.trim() && !taken && !kept ? ['your note'] : []), ...(close?.id && photosSent ? [`${photosSent} photo${photosSent === 1 ? '' : 's'}`] : []), ...(close?.id && voice && !lost.voice ? ['a voice note'] : [])])
      const cap = (w: string): string => `${w[0]!.toUpperCase()}${w.slice(1)}`
      const line = (words: string, warn = false): [string, boolean] => [words, warn]
      const lines: [string, boolean][] = taken
        // Finished already on another phone (409): nothing new was saved but its photos, so it says that, not "sent".
        ? [line(`This day was already finished on another phone${note.trim() ? ', so your note was not added' : ''}.`, true), ...(sent ? [line(`${cap(sent)} added to it`)] : [])]
        : [
            line(`${doneN} job${doneN === 1 ? '' : 's'} done${notDoneN ? `, ${notDoneN} not done` : ''}`),
            ...(cashSaid !== null && cashSaid > 0 ? [line(owner ? `${money(cashSaid)} cash taken` : `${money(cashSaid)} cash to hand over`)] : []),
            ...(close?.invoice_no ? [line(`Invoice ${close.invoice_no}${close.invoice_pence !== null ? ` for ${money(close.invoice_pence)}` : ''} sent to ${getMe()?.businessName ?? 'the owner'}`)] : []),
            ...(sent ? [line(`${cap(sent)} ${owner ? 'kept with the day' : 'sent to the business'}`)] : []),
          ]
      // Kept on the phone: it sends by itself later; photos and a voice note cannot ride with it, and say so.
      if (kept) lines.push(line(`${ack.signedOut ? 'Saved on your phone. It sends once you sign in again' : 'Saved on your phone. It sends when you have signal'}${media ? '; add the photos again from the card then' : ''}.`, true))
      if (lost.photos) lines.push(line(`${lost.photos} photo${lost.photos === 1 ? '' : 's'} did not send: add ${lost.photos === 1 ? 'it' : 'them'} again from the card.`, true))
      if (lost.voice) lines.push(line('The voice note did not send. Tell the business what it said.', true))
      // The sheet was closed while the photos sent: the words still reach them.
      if (!s.dialog.open) { toast(lines.map(([w]) => w).join('. ')); return }
      celebrate(close, lines, taken)
    } catch {
      // Something unexpected: the sheet is never left stuck.
      if (at === 'finishing') letGo('Your day did not send. Try the slide again.')
    } finally {
      sending.remove()
    }
  }

  const celebrate = (close: DayClose | null, lines: [string, boolean][], taken = false): void => {
    at = 'done'
    const shut0 = shutButton(); if (shut0) shut0.disabled = false
    clear(s.head); clear(s.body)
    s.dialog.classList.add('day-close-done')
    const name = first(getMe()?.workerName ?? '')
    s.head.append(el('div', { class: 'day-close-step' }, dayLabel(date)))
    const burst = el('div', { class: 'dc-burst', 'aria-hidden': 'true' }, el('span', { class: 'dc-big-tick ic ic-check' }))
    if (!reducedMotion()) {
      const colours = ['var(--brand-accent)', 'var(--green)', 'var(--brand-ink)', 'var(--brand-accent)']
      for (let i = 0; i < 24; i++) {
        const angle = (i / 24) * Math.PI * 2 + (i % 2 ? 0.17 : -0.09)
        const far = 86 + (i % 3) * 22
        burst.append(el('span', { class: `dc-bit${i % 3 === 0 ? ' dc-dot' : ''}`, style: `--dx:${Math.round(Math.cos(angle) * far)}px;--dy:${Math.round(Math.sin(angle) * far)}px;--r:${(i * 47) % 360}deg;--c:${colours[i % colours.length]};--d:${(i % 4) * 30}ms` }))
      }
    }
    const list = el('ul', { class: 'dc-done-list' })
    lines.forEach(([words, warn], i) => list.append(el('li', { class: warn ? 'is-warn' : '', style: `--i:${i}` }, words)))
    const row = el('div', { class: 'day-close-grid dc-done-actions' })
    if (close?.invoice) {
      const see = el('button', { type: 'button', class: 'btn-secondary' }, 'See my invoice')
      see.addEventListener('click', () => { s.close(); openInvoice(close) })
      row.append(see)
    }
    const shut = el('button', { type: 'button', class: 'btn-primary' }, 'Close')
    shut.addEventListener('click', s.close)
    row.append(shut)
    // Send all payment texts (0093, B15): the owner's finished day carries the card above Close, with people waiting.
    const ask = el('div', { class: 'dc-send-all' })
    s.body.append(burst, el('h2', { class: 'dc-done-title', tabindex: '-1' }, owner || !name ? 'That\'s your day done' : `That's your day done, ${name}`), list, ask, row)
    if (owner && sendAllOn()) void import('./send-all-sheet').then((m) => m.askFinishDay(ask, s.close)).catch(() => { /* no signal for Send all's screens: nothing shows */ })
    // Finished on another phone: that day was saved, but this phone's note was not, so its step stays grey.
    drawBar(close, !taken)
    s.body.querySelector<HTMLElement>('.dc-done-title')?.focus({ preventScroll: true })
    buzz('success')
  }

  step1()
}

// A voice note: recording starts now and runs up to two minutes. Gives back how to stop it (its promise settles once the
// recording is kept), or null when this phone cannot record: then the button says why (a toast sits behind the sheet).
async function recordVoice(button: HTMLButtonElement, kept: (b: Blob) => void): Promise<(() => Promise<void>) | null> {
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) { button.textContent = 'Can\'t record here'; return null }
  let stream: MediaStream
  try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }) } catch { button.textContent = 'Allow the microphone'; return null }
  let rec: MediaRecorder
  const parts: Blob[] = []
  let ended = (): void => {}
  const stopped = new Promise<void>((resolve) => { ended = resolve })
  try {
    rec = new MediaRecorder(stream)
    rec.addEventListener('dataavailable', (e) => { if (e.data.size) parts.push(e.data) })
    rec.addEventListener('stop', () => { stream.getTracks().forEach((t) => t.stop()); kept(new Blob(parts, { type: rec.mimeType || 'audio/mp4' })); ended() })
    rec.start()
  } catch {
    stream.getTracks().forEach((t) => t.stop())
    button.textContent = 'Can\'t record here'
    return null
  }
  button.textContent = 'Recording… tap to stop'
  button.classList.add('is-on', 'voice-recording', 'ic-stop')
  button.classList.remove('ic-mic')
  const stop = (): Promise<void> => { if (rec.state === 'recording') rec.stop(); return stopped }
  setTimeout(() => { void stop() }, 120_000)
  return stop
}

// ---- The invoice ----------------------------------------------------------------------------------------------------
export function invoiceView(close: DayClose): HTMLElement {
  const inv = close.invoice!
  // No logo picked, no logo (as the customer invoice, invoices.ts).
  const logo = close.has_logo ? [el('img', { src: `/api/day-closes/${close.id}/logo`, alt: `${inv.from.name} logo`, class: 'invoice-logo' })] : []
  // The minimum a day (0097) when it is what the day came to.
  const atMin = inv.kind === 'commission' && !!inv.min_pence && inv.pay_pence === inv.min_pence && inv.work_pence !== null && Math.round((inv.work_pence * inv.rate) / 100) < inv.min_pence
  const line = inv.kind === 'day' ? 'Day rate' : atMin ? `Minimum for the day (${inv.rate}% of ${money(inv.work_pence!)} is less)` : inv.work_pence === null ? `${inv.rate}% of the day’s work` : `${inv.rate}% of ${money(inv.work_pence)} work done`
  return el('article', { class: 'invoice' },
    el('div', { class: 'invoice-top' }, ...logo, el('div', { class: 'invoice-title' }, el('strong', {}, 'Invoice'), el('span', {}, `No. ${inv.no} · ${shortDate(inv.date)}`))),
    el('p', { class: 'invoice-from' }, el('strong', {}, inv.from.name), ...(inv.from.address ? [el('br'), inv.from.address] : []), ...(inv.from.utr ? [el('br'), `${roundConfig().country === 'GB' ? 'UTR' : 'Tax no.'} ${inv.from.utr}`] : [])),
    el('p', { class: 'invoice-to' }, el('span', { class: 'muted' }, 'To'), el('br'), el('strong', {}, inv.to.name)),
    el('div', { class: 'invoice-line' }, el('span', {}, `${workTitle()}, ${dayLabel(inv.date)}`, el('br'), el('small', {}, `${line} · ${inv.done_count} job${inv.done_count === 1 ? '' : 's'}: ${inv.jobs.map((j) => j.street || j.name).join(', ')}`)), el('strong', {}, money(inv.pay_pence))),
    el('div', { class: 'invoice-total' }, el('span', {}, 'To pay'), el('span', {}, money(inv.pay_pence))),
    ...(inv.from.bank ? [el('p', { class: 'invoice-pay' }, el('strong', {}, 'Pay by bank'), el('br'), inv.from.bank, el('br'), `Reference: ${inv.no}`)] : []),
    ...(close.invoice_paid_at ? [el('p', { class: 'invoice-paid' }, `Paid ${shortDate(close.invoice_paid_at.slice(0, 10))}`)] : []))
}

export function openInvoice(close: DayClose, onChange?: (c: DayClose) => void): void {
  const s = sheet('Invoice')
  s.head.append(el('h2', {}, `Invoice ${close.invoice_no}`))
  s.body.append(invoiceView(close))
  const row = el('div', { class: 'day-close-grid' })
  if (isOwner() && !close.invoice_paid_at && !close.reopened_at) {
    const paid = el('button', { type: 'button', class: 'btn-primary' }, 'Mark paid')
    paid.addEventListener('click', () => { void answer(close, { invoice_paid: true }, `Invoice ${close.invoice_no} paid`).then((c) => { if (c) { onChange?.(c); s.close() } }) })
    row.append(paid)
  }
  // Save as PDF prints a plain copy of the invoice in the page itself (12 Oct 2026): printing the pop-up printed only the
  // grey shade behind it, a blank grey page. The copy is made as the invoice opens (so its logo has loaded), shows only
  // when printing, and goes when the invoice closes, whether or not the phone says printing has finished.
  document.querySelectorAll('body > .print-invoice').forEach((n) => n.remove())
  const copy = el('div', { class: 'print-invoice' }, invoiceView(close))
  document.body.append(copy)
  document.body.classList.add('printing-invoice')
  s.dialog.addEventListener('close', () => { copy.remove(); if (!document.querySelector('body > .print-invoice')) document.body.classList.remove('printing-invoice') })
  const pdf = el('button', { type: 'button', class: 'btn-secondary' }, 'Save as PDF')
  pdf.addEventListener('click', () => { window.print() })
  row.append(pdf)
  s.body.append(row)
}

// ---- Their invoice details ------------------------------------------------------------------------------------------
export function openInvoiceDetails(saved?: () => void): void {
  const s = sheet('My invoice details')
  s.head.append(el('h2', {}, 'My invoice details'))
  s.body.append(el('p', { class: 'muted' }, 'On the invoice you send the owner when you finish a day.'))
  void api<{ row: MyInvoice }>('/api/me/invoice').then((r) => {
    const row = r.row
    const field = (key: keyof MyInvoice, label: string, hint = ''): HTMLInputElement => {
      const id = `inv-${key}`
      const input = el('input', { id, class: 'day-close-input', ...(hint ? { placeholder: hint } : {}) }) as HTMLInputElement
      input.value = String(row[key] ?? '')
      s.body.append(el('label', { for: id, class: 'day-close-label' }, label), input)
      return input
    }
    const logoRow = el('div', { class: 'day-close-logo-row' })
    const drawLogo = (has: boolean): void => {
      clear(logoRow)
      logoRow.append(has ? el('img', { src: `/api/me/invoice-logo?v=${Date.now()}`, alt: 'Your logo', class: 'invoice-logo' }) : el('div', { class: 'invoice-logo invoice-logo-none' }, 'Logo'))
      const pick = el('input', { type: 'file', accept: 'image/*', class: 'day-close-file', 'aria-label': 'Choose a logo' }) as HTMLInputElement
      const add = el('button', { type: 'button', class: 'btn-secondary' }, has ? 'Change logo' : 'Add logo')
      add.addEventListener('click', () => pick.click())
      pick.addEventListener('change', () => {
        const f = pick.files?.[0]
        if (!f) return
        void shrink(f).then((blob) => blob && mutateUpload({ path: '/api/me/invoice-logo', key: crypto.randomUUID(), fields: {}, file: blob, filename: 'logo.jpg', fileField: 'image', ackField: 'row' }))
          .then((ack) => { if (ack && ack.ok) drawLogo(true); else toast('The logo did not save. Try again.') })
      })
      logoRow.append(add, pick)
    }
    drawLogo(row.has_logo)
    s.body.append(logoRow)
    const name = field('invoice_name', 'Name on invoice', row.worker_name ?? '')
    const address = field('invoice_address', 'Address')
    const bank = field('invoice_bank', 'Pay into', 'Account name, sort code, account number')
    const utr = field('invoice_utr', roundConfig().country === 'GB' ? 'Tax number (UTR, optional)' : 'Tax number (optional)')
    const save = el('button', { type: 'button', class: 'btn-primary' }, 'Save')
    save.addEventListener('click', () => {
      save.disabled = true
      void mutate({ label: 'My invoice details', method: 'PATCH', path: '/api/me/invoice', body: { invoice_name: name.value, invoice_address: address.value, invoice_bank: bank.value, invoice_utr: utr.value } })
        .then((ack) => { save.disabled = false; if (ack.ok || ack.status === 0) { toast('Invoice details saved'); s.close(); saved?.() } else toast(ack.error ?? 'Did not save. Try again.') })
    })
    s.body.append(save)
  }).catch(() => s.body.append(el('p', {}, 'Needs signal to open.')))
}

// A logo, shrunk to fit 600 pixels on its longest side, its shape kept.
export async function shrink(file: File): Promise<Blob | null> {
  try {
    const bitmap = await createImageBitmap(file)
    const k = Math.min(1, 600 / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * k); canvas.height = Math.round(bitmap.height * k)
    const ctx = canvas.getContext('2d'); if (!ctx) return null
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9))
  } catch { return null }
}

// ---- The owner: finished days to look at ------------------------------------------------------------------------
async function answer(close: DayClose, body: Record<string, boolean>, label: string): Promise<DayClose | null> {
  const ack = await mutate<DayClose>({ label, method: 'PATCH', path: `/api/day-closes/${close.id}`, body: { ...body, version: close.version } })
  if (ack.ok && ack.row) { toast(label); return ack.row }
  if (ack.status !== 0) toast(ack.error ?? 'Did not save. Try again.')
  return null
}

export function ownerCloseCard(close: DayClose, changed: () => void): HTMLElement {
  const card = el('section', { class: 'day-close-card day-close-owner', 'aria-label': `${close.worker} finished ${dayLabel(close.date)}` })
  card.append(el('h3', {}, `${close.worker} finished ${dayLabel(close.date)}`))
  card.append(el('div', { class: 'day-close-stats' },
    el('div', {}, el('span', {}, 'Done'), el('strong', {}, `${close.done_count} of ${close.job_count}`)),
    el('div', {}, el('span', {}, 'Cash'), el('strong', {}, money(close.cash_pence ?? close.cash_expected_pence ?? 0))),
    el('div', {}, el('span', {}, 'Invoice'), el('strong', {}, close.invoice_pence !== null ? money(close.invoice_pence) : 'none'))))
  if (close.cash_pence !== null && close.cash_pence !== close.cash_expected_pence) card.append(el('p', { class: 'day-close-warn' }, `Said ${money(close.cash_pence)} cash; Roundly has ${money(close.cash_expected_pence)}.`))
  const notDone = close.jobs.filter((j) => !j.done)
  if (notDone.length) card.append(el('p', {}, el('strong', {}, 'Not done: '), notDone.map((j) => `${j.name}${j.why ? `, ${REASON_WORDS[j.why] ?? j.why}` : ''}`).join('; ')))
  if (close.note) card.append(el('p', { class: 'day-close-said' }, `“${close.note}”`))
  if (close.media.length) {
    const strip = el('div', { class: 'day-close-media' })
    for (const m of close.media) strip.append(m.kind === 'photo' ? el('a', { href: `/api/day-close-media/${m.id}`, target: '_blank', rel: 'noopener' }, el('img', { src: `/api/day-close-media/${m.id}`, alt: 'Photo from the day' })) : el('audio', { controls: '', src: `/api/day-close-media/${m.id}`, preload: 'none' }))
    card.append(strip)
  }
  if (notDone.length) {
    const put = el('button', { type: 'button', class: 'btn-primary' }, notDone.length === 1 ? `Put ${first(notDone[0]!.name)} on another day` : `Put these ${notDone.length} on another day`)
    put.addEventListener('click', () => { void import('./screens/schedule').then(({ focusDiary }) => { focusDiary(notDone.map((j) => j.id), close.date, 'Drag each onto another day, or use Move.', false); window.location.hash = '#/schedule' }) })
    card.append(put)
  }
  const row = el('div', { class: 'day-close-grid' })
  if (close.invoice) {
    const inv = el('button', { type: 'button', class: 'btn-secondary' }, close.invoice_paid_at ? 'Invoice paid' : 'See invoice')
    inv.addEventListener('click', () => openInvoice(close, () => changed()))
    row.append(inv)
  }
  if (!close.cash_received_at && (close.cash_pence ?? close.cash_expected_pence ?? 0) > 0) {
    const cash = el('button', { type: 'button', class: 'btn-secondary' }, 'Cash received')
    cash.addEventListener('click', () => { void answer(close, { cash_received: true }, 'Cash received').then((c) => { if (c) changed() }) })
    row.append(cash)
  }
  if (!close.seen_at) {
    const seen = el('button', { type: 'button', class: 'btn-secondary' }, 'Seen')
    seen.addEventListener('click', () => { void answer(close, { seen: true }, `${close.worker}'s day seen`).then((c) => { if (c) changed() }) })
    row.append(seen)
  }
  card.append(row)
  // A day finished too soon (a commission day before every visit was logged, a slip): they finish it again, its
  // invoice number kept. Only while its invoice is not paid.
  if (close.reopened_at) {
    card.append(el('p', { class: 'muted' }, `Reopened: waiting for ${first(close.worker)} to finish it again.`))
    const back = el('button', { type: 'button', class: 'link-button day-close-link' }, 'Leave it as it was')
    back.addEventListener('click', () => { void answer(close, { reopen: false }, `${dayLabel(close.date)} left as it was`).then((c) => { if (c) changed() }) })
    card.append(back)
  } else if (!close.invoice_paid_at && close.date >= weekBefore(todayISO())) {
    const again = el('button', { type: 'button', class: 'link-button day-close-link' }, `Let ${first(close.worker)} finish it again`)
    again.addEventListener('click', () => { void answer(close, { reopen: true }, `${first(close.worker)} can finish ${dayLabel(close.date)} again`).then((c) => { if (c) changed() }) })
    card.append(again)
  }
  return card
}

// The finished days the owner has not marked seen (the last fortnight): on Today and at the top of the Diary.
let openCloses: DayClose[] | null = null
let readAt = 0
// Read again at most every 20 seconds unless asked (the Diary draws often): a change made here always asks.
export async function readOpenCloses(force = false): Promise<DayClose[]> {
  if (!isOwner()) return []
  if (!force && openCloses && Date.now() - readAt < 20_000) return openCloses
  try { openCloses = (await api<{ closes: DayClose[] }>('/api/day-closes?open=1')).closes; readAt = Date.now() } catch { /* keep the last */ }
  return openCloses ?? []
}
export function finishedDaysArea(container: HTMLElement, place: 'today' | 'diary'): void {
  if (!isOwner()) return
  const slot = el('div', { class: `day-close-area day-close-area-${place}` })
  container.prepend(slot)
  const draw = (list: DayClose[]): void => {
    clear(slot)
    const me = getMe()?.workerName?.trim().toLowerCase()
    const redraw = (): void => { void readOpenCloses(true).then(draw) }
    for (const c of list.filter((x) => x.worker.trim().toLowerCase() !== me)) {
      if (place === 'today' && !c.seen_at) slot.append(ownerCloseCard(c, redraw))
      else if (place === 'today') {
        // Looked at, still waiting on you: an invoice to pay or cash to take in. One line, opening the day.
        const waiting = [c.reopened_at ? 'reopened' : c.invoice_no && !c.invoice_paid_at ? `invoice ${money(c.invoice_pence)} to pay` : null, !c.cash_received_at && (c.cash_pence ?? c.cash_expected_pence ?? 0) > 0 ? `${money(c.cash_pence ?? c.cash_expected_pence ?? 0)} cash to take in` : null].filter(Boolean).join(' · ')
        const line = el('button', { type: 'button', class: 'day-close-banner is-waiting' }, el('strong', {}, `${c.worker} · ${dayLabel(c.date)}`), ` · ${waiting}`)
        line.addEventListener('click', () => {
          const s = sheet(`${c.worker}'s day`)
          s.head.append(el('h2', {}, `${c.worker}'s day`))
          s.body.append(ownerCloseCard(c, () => { s.close(); redraw() }))
        })
        slot.append(line)
      } else if (!c.seen_at) {
        const banner = el('button', { type: 'button', class: 'day-close-banner' }, el('strong', {}, `${c.worker} finished ${dayLabel(c.date)}`), ` · ${c.done_count} of ${c.job_count} done${c.invoice_pence !== null ? ` · invoice ${money(c.invoice_pence)}` : ''}`)
        banner.addEventListener('click', () => {
          const s = sheet(`${c.worker}'s day`)
          s.head.append(el('h2', {}, `${c.worker}'s day`))
          s.body.append(ownerCloseCard(c, () => { s.close(); redraw() }))
        })
        slot.append(banner)
      }
    }
  }
  if (openCloses) draw(openCloses)
  void readOpenCloses().then((l) => { if (slot.isConnected) draw(l) })
}
